// frontend/src/hooks/useShortageData.js
// Reusable hooks for shortage and excess inventory reporting data.

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { sessionApi } from '../services/SessionApiService';
import { apiErrorFrom } from '../utils/parseApiError';
import { useAuth } from '../contexts/AuthContext';
import { kpi as kpiKey, homeTables as homeTablesKey } from '../queries/keys';
import { readResult } from '../queries/api';
import { useTabPollInterval } from '../tabs/tabsContexts';

const THIRTY_SEC = 30 * 1000;
const SIXTY_SEC = 60 * 1000;

/**
 * Fetch shortage KPI data: total shortage value, daily trend.
 * Used by the KPI strip "Projected Stockout" tile.
 *
 * Caching matches the locked-in defaults: 30s staleTime + 60s polling
 * while the tab is on screen. See useInventoryKpi for the rationale.
 */
export function useShortageKpi() {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const refetchInterval = useTabPollInterval(SIXTY_SEC);
  const query = useQuery({
    queryKey: kpiKey.shortage(storeId),
    queryFn: async () => {
      const res = await sessionApi.get('/api/reports/shortage/kpi/');
      if (!res.ok) throw await apiErrorFrom(res);
      return res.json();
    },
    staleTime: THIRTY_SEC,
    refetchInterval,
    enabled: !!storeId,
  });
  return readResult(query, t);
}

/**
 * Fetch shortage items for the home-page dashboard table.
 * Returns items where projected position < 0.
 *
 * Caching: the KPI profile above — 30s staleTime + 60s polling while the
 * tab is on screen — so the table and the KPI chip beside it move together.
 */
export function useShortageItems() {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const refetchInterval = useTabPollInterval(SIXTY_SEC);
  const query = useQuery({
    queryKey: homeTablesKey.shortageItems(storeId),
    queryFn: async () => {
      const res = await sessionApi.get('/api/reports/shortage/items/');
      if (!res.ok) throw await apiErrorFrom(res);
      return res.json();
    },
    staleTime: THIRTY_SEC,
    refetchInterval,
    enabled: !!storeId,
  });
  return readResult(query, t);
}

/**
 * Fetch excess inventory KPI data: total excess value, daily trend.
 * Used by the KPI strip "Excess Inventory" tile.
 *
 * Caching matches the locked-in defaults — see useShortageKpi above.
 */
export function useExcessKpi() {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const refetchInterval = useTabPollInterval(SIXTY_SEC);
  const query = useQuery({
    queryKey: kpiKey.excess(storeId),
    queryFn: async () => {
      const res = await sessionApi.get('/api/reports/excess/kpi/');
      if (!res.ok) throw await apiErrorFrom(res);
      return res.json();
    },
    staleTime: THIRTY_SEC,
    refetchInterval,
    enabled: !!storeId,
  });
  return readResult(query, t);
}
